'use client'

import { useRef, useEffect, useState } from 'react'
import { supabase } from '@/app/_utils/supabase'
import CreatorCard from './CreatorCard'

export default function CreatorsSection({ usernames = [] }) {
    const scrollRef = useRef(null)
    const [creators, setCreators] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const fetchCreators = async () => {
            try {
                // Get unique usernames to fetch
                const uniqueUsernames = [...new Set(usernames)]
                
                const { data, error } = await supabase
                    .from('profiles')
                    .select(`
                        username,
                        avatar_url,
                        custom_avatar_url,
                        first_name,
                        last_name,
                        bio
                    `)
                    .in('username', uniqueUsernames)

                if (error) throw error


                // Transform the data to match our card component needs
                const profileMap = data.reduce((acc, profile) => {
                    acc[profile.username] = {
                        name: [profile.first_name, profile.last_name]
                            .filter(Boolean)
                            .join(' ') || '-',
                        image: profile.custom_avatar_url || profile.avatar_url,
                        username: `@${profile.username}`,
                        bio: profile.bio?.slice(0, 20) + (profile.bio?.length > 20 ? '...' : '') || '-'
                    }
                    return acc
                }, {})

                // Map the original usernames array to get the correct order and duplicates
                const transformedData = usernames.map(username => profileMap[username])
                    .filter(Boolean) // Remove any undefined entries

                setCreators(transformedData)
            } catch (error) {
                console.error('Error fetching creators:', error)
                // Set empty array to show something rather than loading indefinitely
                setCreators([])
            } finally {
                setLoading(false)
            }
        }

        if (usernames.length > 0) {
            fetchCreators()
        } else {
            setLoading(false)
            setCreators([])
        }
    }, [usernames])

    useEffect(() => {
        const scrollContainer = scrollRef.current
        if (!scrollContainer) return

        const scroll = () => {
            scrollContainer.scrollLeft += 1
            if (
                scrollContainer.scrollLeft >=
                scrollContainer.scrollWidth - scrollContainer.clientWidth
            ) {
                scrollContainer.scrollLeft = 0
            }
        }

        const intervalId = setInterval(scroll, 50)

        return () => clearInterval(intervalId)
    }, [])

    // Skip rendering if no usernames provided
    if (usernames.length === 0) return null

    return (
        <div className="bg-neutral-950 bg-opacity-50 py-16 relative z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                    <h2 className="text-base text-indigo-400 font-semibold tracking-wide uppercase">
                        we're just getting started
                    </h2>
                    <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-white sm:text-4xl">
                        but heres some early focuses:
                    </p>
                </div>

                {/* Horizontal Scroll Container */}
                <div className="relative">
                    {/* Gradient Fade Effect - Left */}

                    {/* Scrolling Content */}
                    <div
                        ref={scrollRef}
                        className="flex overflow-x-auto space-x-8 pb-8 scrollbar-hide"
                    >
                        {loading ? (
                            <CreatorCard.Skeleton count={usernames.length} />
                        ) : creators && creators.length > 0 ? (
                            creators.map((creator, index) => (
                                <CreatorCard 
                                    key={`${creator.username}-${index}`} 
                                    creator={creator} 
                                />
                            ))
                        ) : (
                            <div className="text-gray-400 text-center w-full py-8">
                                No creators found
                            </div>
                        )}
                    </div>

                    {/* Gradient Fade Effect - Right */}
                </div>
            </div>
        </div>
    )
}
